import React, { useState } from "react";
import MirroredBars from "./MirroredBars";
import { FormStrip } from "./TeamPanels";
import { ShotMap, Unavailable } from "./FootballPitch";
import { formatNumber, groupMetrics, isInPenaltyArea, metricLabel, summariseShots } from "./fiData";
import "./fi-extra.css";

const STATUS_LABELS = { not_started: "Pre-match", finished: "Full time", live: "In play" };
export const statusLabel = (status) => STATUS_LABELS[status] || (status ? String(status).replace(/_/g, " ") : "Status unknown");

// Match-level metrics worth comparing at a glance, in reading order.
const HEADLINE_KEYS = [
  "xgf", "npxgf", "shots_for", "corners_for",
  "territory.possession_pct", "territory.field_tilt", "territory.box_entries",
  "defending.ppda", "passing.passes", "passing.pass_accuracy", "possession_value.xt_total",
];

function Crest({ team }) {
  const [failed, setFailed] = useState(false);
  if (team?.image_url && !failed) {
    return <img src={team.image_url} alt="" width="56" height="56" style={{ objectFit: "contain" }} onError={() => setFailed(true)} />;
  }
  return <div className="fi-monogram" style={{ width: 56, height: 56, fontSize: 20 }}>{team?.name?.slice(0, 2)}</div>;
}

/** Teams, score (only when recorded), status, kickoff and competition. */
export function MatchHeader({ profile, competition }) {
  const hasScore = typeof profile.home_goals === "number" && typeof profile.away_goals === "number";
  const kickoff = profile.kickoff_utc
    ? new Date(profile.kickoff_utc).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
    : null;
  return (
    <header className="fi-hero" style={{ alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
        <Crest team={profile.home_team} />
        <div>
          <div className="fi-eyebrow">PRIVATE RESEARCH · SOURCE ACTUALS</div>
          <h2>Match intelligence</h2>
          <p>
            {profile.name} · {statusLabel(profile.status)}
            {hasScore ? ` · ${profile.home_goals}–${profile.away_goals}` : ""}
            {kickoff ? ` · ${kickoff}` : ""}
            {competition ? ` · ${competition}` : ""}
          </p>
        </div>
        <Crest team={profile.away_team} />
      </div>
    </header>
  );
}

/** Pre-match: both teams' form and means before kickoff, side by side. */
export function HeadToHead({ home, away, homeName, awayName }) {
  const usable = (side) => side && side.status === "AVAILABLE" && side.metrics;
  if (!usable(home) && !usable(away)) {
    return (
      <div className="fi-card">
        <h3>Head to head: form going in</h3>
        <p className="fx-sub">No verified team form is cached for either side before this kickoff.</p>
      </div>
    );
  }
  const rows = HEADLINE_KEYS
    .filter((key) => home?.metrics?.[key] || away?.metrics?.[key])
    .map((key) => ({
      key,
      label: metricLabel(key),
      a: home?.metrics?.[key]?.value ?? null,
      b: away?.metrics?.[key]?.value ?? null,
      na: home?.metrics?.[key]?.sample ?? 0,
      nb: away?.metrics?.[key]?.sample ?? 0,
    }));
  return (
    <div className="fi-card">
      <h3>Head to head: form going in</h3>
      <p className="fx-sub">Match means over each team's most recent cached competition matches before kickoff. Target-match and later results are excluded.</p>
      <div className="fx-pair" style={{ margin: "0 0 18px" }}>
        <div><div className="fx-embedded-ink" style={{ fontSize: 12, marginBottom: 6 }}>{homeName} · {home?.sample_size ?? 0} matches</div><FormStrip matches={home?.matches || []} /></div>
        <div><div className="fx-embedded-ink" style={{ fontSize: 12, marginBottom: 6 }}>{awayName} · {away?.sample_size ?? 0} matches</div><FormStrip matches={away?.matches || []} /></div>
      </div>
      <div className="fx-key"><span className="a">● {homeName}</span><span className="b">{awayName} ●</span></div>
      <MirroredBars rows={rows} nameA={homeName} nameB={awayName} mean />
    </div>
  );
}

/** Post-match: this match's recorded team values side by side, grouped. */
export function ActualsComparison({ actuals, homeName, awayName, headlineOnly = false }) {
  const home = actuals?.home?.values;
  const away = actuals?.away?.values;
  if (!home && !away) {
    return <Unavailable text="No recorded team values for this match in the cached data." />;
  }
  if (headlineOnly) {
    const rows = HEADLINE_KEYS
      .filter((key) => typeof home?.[key] === "number" || typeof away?.[key] === "number")
      .map((key) => ({ key, label: metricLabel(key), a: home?.[key] ?? null, b: away?.[key] ?? null }));
    return (
      <div>
        <div className="fx-key"><span className="a">● {homeName}</span><span className="b">{awayName} ●</span></div>
        <MirroredBars rows={rows} nameA={homeName} nameB={awayName} />
        <p className="fi-note">Headline values recorded for this match. Basic statistics are on Stats; advanced values are under Team detail.</p>
      </div>
    );
  }
  const asMetrics = (values = {}) => Object.fromEntries(Object.entries(values).filter(([k, v]) => typeof v === "number" && !k.endsWith("_against") && !["xga", "npxga"].includes(k)).map(([k, v]) => [k, { value: v }]));
  const groups = groupMetrics("team", { ...asMetrics(away), ...asMetrics(home) });
  return (
    <div>
      <div className="fx-key"><span className="a">● {homeName}</span><span className="b">{awayName} ●</span></div>
      {groups.map((group) => {
        const rows = group.items.map(({ key, label }) => ({ key, label, a: home?.[key] ?? null, b: away?.[key] ?? null }));
        return (
          <section key={group.id} style={{ marginBottom: 18 }}>
            <h4 style={{ margin: "0 0 4px", fontSize: 13 }}>{group.title}</h4>
            <MirroredBars rows={rows} nameA={homeName} nameB={awayName} />
          </section>
        );
      })}
      <p className="fi-note">Values recorded for this one match. Nothing is inferred from recent form.</p>
    </div>
  );
}

/** Both teams' located shots: summary bars plus one pitch each. */
export function ShotComparison({ shots, home, away }) {
  const homeShots = shots.filter((s) => s.team_id === home?.id);
  const awayShots = shots.filter((s) => s.team_id === away?.id);
  if (!homeShots.length && !awayShots.length) return <Unavailable text="No located shots in the selected sample." />;
  const h = summariseShots(homeShots);
  const a = summariseShots(awayShots);
  const rows = [
    { key: "shots_for", label: "Shots", a: h.count, b: a.count },
    { key: "goals", label: shots.some(s=>s.is_own_goal) ? "Goals (excluding own goals)" : "Goals", a: h.goals, b: a.goals },
    { key: "xgf", label: "Total xG", a: h.xg, b: a.xg },
    { key: "xg_per_shot", label: "xG per shot", a: h.xgPerShot, b: a.xgPerShot },
    { key: "box", label: "Shots inside the box", a: homeShots.filter(isInPenaltyArea).length, b: awayShots.filter(isInPenaltyArea).length },
  ];
  const side = (team, list, summary) => (
    <div className="fi-card" key={team?.id}>
      <h3>{team?.name}</h3>
      <p className="fx-sub">{summary.count} shots · {summary.goals} goals · {formatNumber(summary.xg)} xG</p>
      <ShotMap shots={list} half />
    </div>
  );
  return (
    <div>
      <div className="fx-key"><span className="a">● {home?.name}</span><span className="b">{away?.name} ●</span></div>
      <MirroredBars rows={rows} nameA={home?.name} nameB={away?.name} />
      {shots.some(s=>s.is_own_goal)&&<p className="fi-note">Own goals are labelled on the acting player’s team map and excluded from attacking-goal counts. The scoreboard includes the goal for the opposition.</p>}
      <div className="fx-pair" style={{ margin: "18px 0 0" }}>
        {side(home, homeShots, h)}
        {side(away, awayShots, a)}
      </div>
    </div>
  );
}
